/**
 * Pos-emissao: endossos, cancelamentos, renovacoes e sinistros.
 *
 * Reexportado por `../resources.ts`. Ver docs/FRONTEND.md, secao 3.
 */

import { query, type ListParams, type Money } from './comum';
import type { Policy } from './portfolio';

import { api, requestPage } from '../client';

/** Situacao de uma solicitacao que passa por aprovacao. */
export type RequestStatus = 'solicitado' | 'aprovado' | 'recusado';

export type EndorsementKind = 'valor' | 'prazo' | 'tomador' | 'dados';

/**
 * Situacao do endosso (P-END-1). Alem das da solicitacao: o rascunho do assistente, o
 * endosso em analise na esteira (operacao do tipo endosso), o emitido pela operacao e o
 * cancelado pela desistencia de quem pediu.
 */
export type EndorsementStatus =
  RequestStatus | 'rascunho' | 'em_analise' | 'emitido' | 'cancelado';

export interface Endorsement {
  id: string;
  number: number;
  policy_id: string;
  /** Vazio no endosso do assistente, que tem tipo e subtipo do catalogo. */
  kind: EndorsementKind | '';
  reason: string;
  status: EndorsementStatus;
  amount_delta: Money | null;
  new_coverage_end: string | null;
  requested_label: string;
  decided_label: string;
  decided_at: string | null;
  decision_note: string;
  created_at: string;
  // Catalogo e assistente de endosso (P-END-1).
  endorsement_type_id?: string | null;
  endorsement_subtype_id?: string | null;
  endorsement_reason_id?: string | null;
  operation_id?: string | null;
  object_edit_mode?: '' | 'dados' | 'texto';
  object_text?: string | null;
  object_tag_values?: Record<string, string>;
  summary_confirmed?: boolean;
  summary_confirmed_at?: string | null;
  submitted_at?: string | null;
  submitted_label?: string;
  withdrawn_at?: string | null;
  withdrawn_label?: string;
  withdrawal_note?: string;
  decided_by_requester?: boolean;
}

export interface EndorsementInput {
  kind: EndorsementKind;
  reason: string;
  amount_delta?: Money | null;
  new_coverage_end?: string | null;
}

export interface RequestDecisionInput {
  status: RequestStatus;
  note?: string;
}

export interface CancellationRequest {
  id: string;
  policy_id: string;
  reason: string;
  status: RequestStatus;
  effective_date: string | null;
  requested_label: string;
  decided_label: string;
  decided_at: string | null;
  decision_note: string;
  created_at: string;
}

export type RenewalStatus = 'pendente' | 'em_cotacao' | 'renovada' | 'nao_renovada';

export interface Renewal {
  id: string;
  policy_id: string;
  status: RenewalStatus;
  new_operation_id: string | null;
  owner_label: string;
  notes: string;
  decided_at: string | null;
  created_at: string;
}

/**
 * Uma apolice da fila com o acompanhamento resolvido.
 *
 * Acompanhamento nulo significa que ninguem assumiu a apolice ainda, e nao que ela
 * dispense renovacao.
 */
export interface RenewalItem {
  policy: Policy;
  renewal: Renewal | null;
  days_to_expiry: number;
  /**
   * Janela que pos a apolice na fila e de onde ela veio: o prazo da modalidade nos
   * parametros da renovacao em vigor, a janela padrao (90 dias) ou a informada (P-PAR-3).
   */
  window_days: number;
  window_source: 'modalidade' | 'padrao' | 'informada';
}

export interface RenewalInput {
  status: RenewalStatus;
  new_operation_id?: string | null;
  notes?: string;
  take_ownership?: boolean;
}

export type ClaimStatus = 'aberto' | 'em_analise' | 'pago' | 'negado';

export interface Claim {
  id: string;
  number: number;
  policy_id: string;
  kind: string;
  description: string;
  claimed_amount: Money;
  paid_amount: Money;
  status: ClaimStatus;
  occurrence_date: string | null;
  notice_date: string;
  owner_label: string;
  decided_label: string;
  decided_at: string | null;
  decision_note: string;
  created_at: string;
}

export interface ClaimInput {
  kind: string;
  description: string;
  claimed_amount: Money;
  occurrence_date?: string | null;
}

export interface ClaimDecisionInput {
  status: ClaimStatus;
  paid_amount?: Money | null;
  note?: string;
}

export const endorsements = {
  list: (params: ListParams, signal?: AbortSignal) =>
    requestPage<EndorsementDetail>(`/api/v1/endorsements${query(params)}`, signal),
  request: (policyId: string, input: EndorsementInput) =>
    api.post<Endorsement>(`/api/v1/policies/${policyId}/endorsements`, input),
  decide: (id: string, input: RequestDecisionInput) =>
    api.post<Endorsement>(`/api/v1/endorsements/${id}/decision`, input),
};

export const cancellations = {
  list: (params: ListParams, signal?: AbortSignal) =>
    requestPage<CancellationRequest>(`/api/v1/cancellations${query(params)}`, signal),
  request: (policyId: string, reason: string) =>
    api.post<CancellationRequest>(`/api/v1/policies/${policyId}/cancellation`, {
      reason,
    }),
  decide: (id: string, input: RequestDecisionInput) =>
    api.post<CancellationRequest>(`/api/v1/cancellations/${id}/decision`, input),
};

export const renewals = {
  list: (params: ListParams, signal?: AbortSignal) =>
    requestPage<RenewalItem>(`/api/v1/renewals${query(params)}`, signal),
  track: (policyId: string, input: RenewalInput) =>
    api.patch<Renewal>(`/api/v1/policies/${policyId}/renewal`, input),
};

/**
 * A contagem por situacao vem em `meta`, calculada no banco. Contar apenas o que
 * esta na pagina mostraria um numero que muda ao virar a pagina.
 */
export async function listClaims(
  params: ListParams,
  signal?: AbortSignal,
): Promise<{
  items: Claim[];
  total: number;
  limit: number;
  offset: number;
  byStatus: Record<string, number>;
}> {
  const page = await requestPage<Claim>(`/api/v1/claims${query(params)}`, signal);
  const byStatus = (page.meta.by_status as Record<string, number> | undefined) ?? {};
  return { ...page, byStatus };
}

export const claims = {
  list: listClaims,
  register: (policyId: string, input: ClaimInput) =>
    api.post<Claim>(`/api/v1/policies/${policyId}/claims`, input),
  decide: (id: string, input: ClaimDecisionInput) =>
    api.post<Claim>(`/api/v1/claims/${id}/decision`, input),
};

// --- Catalogo de endosso e endosso do assistente (P-END-1) -------------------------
//
// Ver docs/ADR/0044-catalogo-de-endosso-e-endosso-neutro.md.

export type EndorsementTypeCode =
  'neutro' | 'alteracao' | 'cancelamento' | 'aniversario';
export type EndorsementFlowMode = 'operacao' | 'solicitacao';
export type EndorsementSegregationMode = 'registrar' | 'recusar';
export type EndorsementChangeScope = 'tomador' | 'segurado' | 'apolice';
export type EndorsementChangeField =
  | 'legal_name'
  | 'social_name'
  | 'trade_name'
  | 'address'
  | 'insured_amount'
  | 'coverage_start'
  | 'coverage_end'
  | 'complementary_insured_amount'
  | 'object_text'
  | 'particular_clause';

export interface EndorsementType {
  id: string;
  code: EndorsementTypeCode;
  name: string;
  description: string;
  enabled: boolean;
  premium_movement: boolean;
  sort_order: number;
  /** O envio depende de calculo nao homologado (P-END-2, P-PAR-3). */
  calculation_pending: boolean;
  can_request: boolean;
  active_subtypes: number;
  updated_label: string;
  updated_at: string | null;
}

export interface EndorsementSubtype {
  id: string;
  type_id: string;
  name: string;
  description: string;
  active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface EndorsementObjectTag {
  name: string;
  label: string;
  kind: 'automatico' | 'texto';
}

export interface EndorsementReason {
  id: string;
  subtype_id: string;
  name: string;
  active: boolean;
  sort_order: number;
  object_template: string;
  object_tags: EndorsementObjectTag[];
  created_at: string;
  updated_at: string;
}

export interface EndorsementReasonDocument {
  id: string;
  document_name: string;
  required: boolean;
  sort_order: number;
  document_type_id: string | null;
}

export interface EndorsementSettings {
  flow_mode: EndorsementFlowMode;
  segregation_mode: EndorsementSegregationMode;
  updated_label: string;
  updated_at: string | null;
}

export interface EndorsementAddress {
  zip_code: string;
  street: string;
  number: string;
  complement: string;
  district: string;
  city: string;
  state: string;
}

/** Um lado da alteracao: um so campo vem preenchido, conforme o dado. */
export interface EndorsementChangeValue {
  text?: string;
  address?: EndorsementAddress;
  amount?: Money;
  date?: string;
}

export interface EndorsementChangeInput {
  scope: EndorsementChangeScope;
  field: EndorsementChangeField;
  after: EndorsementChangeValue;
}

export interface EndorsementChange {
  scope: EndorsementChangeScope;
  field: EndorsementChangeField;
  before: EndorsementChangeValue;
  after: EndorsementChangeValue;
  changed: boolean;
}

export interface EndorsementDocument {
  id: string;
  number: number;
  title: string;
  group: string;
  required: boolean;
  status: 'pendente' | 'recebido' | 'dispensado';
  current_version: number;
  filename: string;
  updated_at: string;
}

export interface EndorsementProblem {
  step: string;
  field: string;
  message: string;
}

/** O endosso com o catalogo, a apolice, a operacao, as alteracoes e os documentos. */
export interface EndorsementDetail extends Endorsement {
  endorsement_type?: { id: string; code: EndorsementTypeCode; name: string } | null;
  endorsement_subtype?: { id: string; name: string } | null;
  endorsement_reason?: { id: string; name: string } | null;
  policy?: {
    id: string;
    number: number;
    external_number: string;
    status: string;
    policyholder_name: string;
    policyholder_document: string;
  } | null;
  operation?: {
    id: string;
    number: number;
    stage: string;
    proposal_status: string;
    decline_reason: string;
  } | null;
  calculation_pending?: boolean;
  flow_mode?: EndorsementFlowMode | '';
  changes?: EndorsementChange[];
  documents?: EndorsementDocument[];
  required_documents?: EndorsementReasonDocument[];
  problems?: EndorsementProblem[];
  /** Endosso de alteracao e de cancelamento (P-END-2); nulo nos outros e na fila. */
  financial?: EndorsementFinancial | null;
}

export interface EndorsementDraftInput {
  endorsement_reason_id?: string | null;
  reason?: string;
  changes?: EndorsementChangeInput[];
  object_edit_mode?: '' | 'dados' | 'texto';
  object_text?: string | null;
  object_tag_values?: Record<string, string>;
  summary_confirmed?: boolean;
  /** Data de cancelamento (P-END-2), dentro da vigencia. Nulo limpa. */
  effective_date?: string | null;
  /** Valor solicitado (P-END-2). Nulo limpa. */
  requested_premium?: Money | null;
}

export interface EndorsementStartInput extends EndorsementDraftInput {
  endorsement_subtype_id: string;
}

export interface EndorsementEligibility {
  eligible: boolean;
  reasons: string[];
  type: EndorsementType | null;
  open_endorsement: Endorsement | null;
  drafts: Endorsement[];
  calculation_pending: boolean;
  flow_mode: EndorsementFlowMode;
}

export interface EndorsementPolicyObject {
  policy_id: string;
  text: string;
  initial_text: string;
  edit_modes: string[];
}

export interface EndorsementParticularClause {
  id: string;
  name: string;
  body: string;
  free_text: boolean;
  broker_can_select: boolean;
  version: number;
}

export interface EndorsementValidation {
  valid: boolean;
  step: string;
  problems: EndorsementProblem[];
}

export interface EndorsementFileLink {
  url: string;
  filename: string;
  expires_at: string;
}

export interface EndorsementUploadTicket {
  version_id: string;
  version: number;
  upload_url: string;
  expires_at: string;
}

const endossoBase = '/api/v1/endorsements';

/** O endosso do assistente: rascunho, envio, desistencia e documentos. */
export const endorsementWizard = {
  start: (policyId: string, input: EndorsementStartInput) =>
    api.post<EndorsementDetail>(`/api/v1/policies/${policyId}/endorsements`, input),
  get: (id: string, signal?: AbortSignal) =>
    api.get<EndorsementDetail>(`${endossoBase}/${id}`, signal),
  update: (id: string, input: EndorsementDraftInput) =>
    api.patch<EndorsementDetail>(`${endossoBase}/${id}`, input),
  validate: (id: string, step = '') =>
    api.post<EndorsementValidation>(`${endossoBase}/${id}/validation`, { step }),
  changes: (id: string, onlyChanged = false, signal?: AbortSignal) =>
    api.get<EndorsementChange[]>(
      `${endossoBase}/${id}/changes${query({ only_changed: onlyChanged || undefined })}`,
      signal,
    ),
  submit: (id: string) =>
    api.post<EndorsementDetail>(`${endossoBase}/${id}/submission`, {}),
  withdraw: (id: string, note: string) =>
    api.post<EndorsementDetail>(`${endossoBase}/${id}/withdrawal`, { note }),
  documents: (id: string, signal?: AbortSignal) =>
    api.get<EndorsementDocument[]>(`${endossoBase}/${id}/documents`, signal),
  requireDocument: (id: string, title: string) =>
    api.post<EndorsementDocument>(`${endossoBase}/${id}/documents`, { title }),
  startUpload: (
    id: string,
    documentId: string,
    input: {
      filename: string;
      content_type: string;
      byte_size: number;
      checksum: string;
    },
  ) =>
    api.post<EndorsementUploadTicket>(
      `${endossoBase}/${id}/documents/${documentId}/uploads`,
      input,
    ),
  confirmUpload: (id: string, documentId: string, versionId: string) =>
    api.post<EndorsementDocument>(
      `${endossoBase}/${id}/documents/${documentId}/uploads/${versionId}/confirmation`,
      {},
    ),
  download: (id: string, documentId: string) =>
    api.get<EndorsementFileLink>(
      `${endossoBase}/${id}/documents/${documentId}/download`,
    ),
  latestDocument: (id: string) =>
    api.get<EndorsementFileLink>(`${endossoBase}/${id}/document`),
};

/** Catalogo de endosso, configuracao do fluxo e leituras da apolice para o assistente. */
export const endorsementCatalog = {
  types: (enabledOnly = false, signal?: AbortSignal) =>
    api.get<EndorsementType[]>(
      `/api/v1/endorsement-types${query({ enabled: enabledOnly || undefined })}`,
      signal,
    ),
  updateType: (
    id: string,
    input: Partial<
      Pick<EndorsementType, 'name' | 'description' | 'enabled' | 'sort_order'>
    >,
  ) => api.patch<EndorsementType>(`/api/v1/endorsement-types/${id}`, input),
  subtypes: (typeId?: string, includeInactive = false, signal?: AbortSignal) =>
    api.get<EndorsementSubtype[]>(
      `/api/v1/endorsement-subtypes${query({
        type_id: typeId,
        include_inactive: includeInactive || undefined,
      })}`,
      signal,
    ),
  createSubtype: (input: {
    type_id: string;
    name: string;
    description?: string;
    sort_order?: number;
  }) => api.post<EndorsementSubtype>('/api/v1/endorsement-subtypes', input),
  updateSubtype: (
    id: string,
    input: Partial<
      Pick<EndorsementSubtype, 'name' | 'description' | 'active' | 'sort_order'>
    >,
  ) => api.patch<EndorsementSubtype>(`/api/v1/endorsement-subtypes/${id}`, input),
  reasons: (subtypeId: string, includeInactive = false, signal?: AbortSignal) =>
    api.get<EndorsementReason[]>(
      `/api/v1/endorsement-subtypes/${subtypeId}/reasons${query({
        include_inactive: includeInactive || undefined,
      })}`,
      signal,
    ),
  createReason: (
    subtypeId: string,
    input: Partial<
      Pick<EndorsementReason, 'name' | 'sort_order' | 'object_template' | 'object_tags'>
    >,
  ) =>
    api.post<EndorsementReason>(
      `/api/v1/endorsement-subtypes/${subtypeId}/reasons`,
      input,
    ),
  updateReason: (
    id: string,
    input: Partial<
      Pick<
        EndorsementReason,
        'name' | 'active' | 'sort_order' | 'object_template' | 'object_tags'
      >
    >,
  ) => api.patch<EndorsementReason>(`/api/v1/endorsement-reasons/${id}`, input),
  reasonDocuments: (reasonId: string, signal?: AbortSignal) =>
    api.get<EndorsementReasonDocument[]>(
      `/api/v1/endorsement-reasons/${reasonId}/documents`,
      signal,
    ),
  replaceReasonDocuments: (
    reasonId: string,
    documents: { document_name: string; required: boolean; sort_order?: number }[],
  ) =>
    api.put<EndorsementReasonDocument[]>(
      `/api/v1/endorsement-reasons/${reasonId}/documents`,
      { documents },
    ),
  settings: (signal?: AbortSignal) =>
    api.get<EndorsementSettings>('/api/v1/endorsement-settings', signal),
  updateSettings: (
    input: Partial<Pick<EndorsementSettings, 'flow_mode' | 'segregation_mode'>>,
  ) => api.patch<EndorsementSettings>('/api/v1/endorsement-settings', input),
  eligibility: (policyId: string, type: EndorsementTypeCode, signal?: AbortSignal) =>
    api.get<EndorsementEligibility>(
      `/api/v1/policies/${policyId}/endorsement-eligibility${query({ type })}`,
      signal,
    ),
  policyObject: (policyId: string, signal?: AbortSignal) =>
    api.get<EndorsementPolicyObject>(`/api/v1/policies/${policyId}/object`, signal),
  policyClauses: (policyId: string, signal?: AbortSignal) =>
    api.get<EndorsementParticularClause[]>(
      `/api/v1/policies/${policyId}/particular-clauses`,
      signal,
    ),
};

// --- Endosso com efeito financeiro (P-END-2) --------------------------------------
//
// Ver docs/ADR/0059-endosso-financeiro-e-cancelamento-como-endosso.md.
// So o que a operacao do tipo endosso nao tem: premio, taxa, comissao,
// parcelas, termo, motores, minuta e emissao sao as rotas da operacao (P-COT-4).

/** Acao bloqueada ate a homologacao das regras dela. */
export interface EndorsementFinancialRuleSet {
  available: boolean;
  action: string;
  pending_rules: string[];
  message: string;
}

/** Conta do tomador para a restituicao, ou o retrato da conta indicada. */
export interface EndorsementRefundAccount {
  id: string;
  bank_code: string;
  account_type: string;
  agency: string;
  agency_digit: string;
  account: string;
  account_digit: string;
  payment_method: string;
}

export interface EndorsementFinancialCommission {
  id: string;
  amount: Money;
  rate_bps: number;
  competence: string;
  status: string;
  manual_reason: string;
  created_at: string;
}

/** O que o endosso de alteracao e o de cancelamento tem e a operacao nao tem. */
export interface EndorsementFinancial {
  effective_date: string | null;
  /** Nulo ate a regra de restituicao (END-005, PRC-010). */
  estimated_refund: Money | null;
  /** Nulo ate a regra do premio do endosso (PRC-Q). */
  premium_direction: 'cobrar' | 'restituir' | 'sem_movimento' | null;
  requested_premium: Money | null;
  review_requested: boolean;
  review_suggested: Money | null;
  review_justification: string;
  review_requested_at: string | null;
  review_requested_label: string;
  refund_bank_account_id: string | null;
  refund_bank_account: EndorsementRefundAccount | null;
  account_required: boolean;
  policy_coverage_start: string;
  policy_coverage_end: string;
  premium_rules: EndorsementFinancialRuleSet;
  commission_rules: EndorsementFinancialRuleSet;
  issuance_rules: EndorsementFinancialRuleSet;
  commissions: EndorsementFinancialCommission[];
}

export interface EndorsementRefundAccountList {
  endorsement_id: string;
  policyholder_id: string;
  accounts: EndorsementRefundAccount[];
  chosen: EndorsementRefundAccount | null;
  chosen_available: boolean;
  account_required: boolean;
}

export interface EndorsementRefundPolicyEligibility {
  policy_id: string;
  validated: boolean;
  eligible: boolean | null;
  policy_status: string;
  coverage_start: string;
  coverage_end: string;
  premium: Money;
  installments: {
    total: number;
    open: number;
    paid: number;
    overdue: number;
    cancelled: number;
  };
  open_cancellation_request: boolean;
  open_cancellation_endorsement: boolean;
  rules: EndorsementFinancialRuleSet;
}

export interface EndorsementRefundEligibility {
  endorsement_id: string;
  endorsement_type: string;
  status: string;
  validated: boolean;
  eligible: boolean | null;
  effective_date: string | null;
  account_required: boolean;
  account_chosen: boolean;
  rules: EndorsementFinancialRuleSet;
}

/** Restituicao, revisao de valor e conta do endosso financeiro. */
export const endorsementFinancial = {
  refundAccounts: (id: string, signal?: AbortSignal) =>
    api.get<EndorsementRefundAccountList>(
      `${endossoBase}/${id}/refund-accounts`,
      signal,
    ),
  chooseRefundAccount: (id: string, bankAccountId: string | null) =>
    api.put<EndorsementDetail>(`${endossoBase}/${id}/refund-account`, {
      bank_account_id: bankAccountId,
    }),
  premiumReview: (
    id: string,
    input: { suggested_amount: Money; justification: string },
  ) => api.post<EndorsementDetail>(`${endossoBase}/${id}/premium-review`, input),
  refundCalculation: (id: string) =>
    api.post<void>(`${endossoBase}/${id}/refund-calculation`, {}),
  refundEligibility: (id: string, signal?: AbortSignal) =>
    api.get<EndorsementRefundEligibility>(
      `${endossoBase}/${id}/refund-eligibility`,
      signal,
    ),
  policyRefundEstimate: (policyId: string, signal?: AbortSignal) =>
    api.get<void>(`/api/v1/policies/${policyId}/refund-estimate`, signal),
  policyRefundEligibility: (policyId: string, signal?: AbortSignal) =>
    api.get<EndorsementRefundPolicyEligibility>(
      `/api/v1/policies/${policyId}/refund-eligibility`,
      signal,
    ),
};
